import { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Activity, Loader2, Cpu } from "lucide-react";
import { toast } from "sonner";
import {
  getAgentStatus,
  startServer,
  stopServer,
} from "@/lib/lisControlAgent";

// Equipos mostrados en el panel (deben coincidir con SERVERS del agente)
const ANALYZERS = [
  { key: "dymind", name: "Dymind DF55", desc: "Hematología · Puerto 5000" },
  { key: "zybio", name: "Zybio EXC200", desc: "Química Clínica · Puerto 5001" },
  { key: "lifotronic", name: "Lifotronic ECL8000", desc: "Inmunología · Puerto 5002" },
  { key: "eaglenos", name: "Eaglenos P1", desc: "Electrolitos · Puerto 5003" },
  { key: "finecare", name: "Finecare FS-205", desc: "Inmunofluorescencia · Puerto 5004" },
  { key: "ocg", name: "Wondfo OCG-102", desc: "Coagulación · Puerto 5005" },
];

export const AnalyzerControl = () => {
  const [available, setAvailable] = useState(true);
  const [servers, setServers] = useState({});
  const [busy, setBusy] = useState(null); // key en proceso
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    const res = await getAgentStatus();
    setAvailable(res.available);
    setServers(res.servers || {});
    setLoaded(true);
  }, []);

  useEffect(() => {
    refresh();
    timerRef.current = setInterval(refresh, 5000);
    return () => clearInterval(timerRef.current);
  }, [refresh]);

  const handleToggle = async (key, turnOn) => {
    setBusy(key);
    const label = ANALYZERS.find((x) => x.key === key)?.name || key;
    try {
      const result = turnOn ? await startServer(key) : await stopServer(key);
      if (result.success) {
        toast.success(result.message || (turnOn ? "Encendido" : "Apagado"));
      } else {
        const msg = (result.message || "").toLowerCase();
        if (msg.includes("invalido") || msg.includes("inválido")) {
          toast.error(
            `"${label}" no está registrado en el Agente de Control de esta PC. Actualiza el archivo lis_control_agent.py en la PC de recepción y reinicia el agente.`,
            { duration: 8000 }
          );
        } else {
          toast.error(result.message || "No se pudo cambiar el estado");
        }
      }
    } catch {
      toast.error("No se pudo contactar el Agente de Control");
      setAvailable(false);
    } finally {
      await refresh();
      setBusy(null);
    }
  };

  return (
    <Card data-testid="analyzer-control-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
          <Cpu className="h-5 w-5 text-[#0284C7]" />
          Equipos / Analizadores
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ANALYZERS.map((a) => {
            const running = !!servers[a.key]?.running;
            const isBusy = busy === a.key;
            return (
              <div
                key={a.key}
                className="flex items-center justify-between gap-4 rounded-lg bg-slate-50 p-4 border border-slate-100"
                data-testid={`analyzer-row-${a.key}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={`relative flex h-3 w-3 shrink-0`}
                    data-testid={`analyzer-status-${a.key}`}
                    data-running={running}
                  >
                    {available && running && (
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                    )}
                    <span
                      className={`relative inline-flex rounded-full h-3 w-3 ${
                        !available
                          ? "bg-slate-300"
                          : running
                          ? "bg-emerald-500"
                          : "bg-rose-500"
                      }`}
                    />
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{a.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {a.desc}
                    </p>
                    <p
                      className={`text-xs mt-0.5 font-medium ${
                        !available
                          ? "text-slate-400"
                          : running
                          ? "text-emerald-600"
                          : "text-rose-600"
                      }`}
                    >
                      {!available
                        ? "Sin conexión"
                        : running
                        ? "Activo"
                        : "Detenido"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isBusy && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  <Switch
                    checked={running}
                    disabled={!available || isBusy}
                    onCheckedChange={(v) => handleToggle(a.key, v)}
                    data-testid={`analyzer-switch-${a.key}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
